import { log } from '../app/logger.js';

/**
 * Converts latitude/longitude to pixel coordinates.
 */
class Coords {
  /**
   * Return in world coordinates.
   */
  static toWorld(lat, lng) {
    // The mapping between latitude, longitude and pixels is defined by the web
    // mercator projection. See
    // https://developers.google.com/maps/documentation/javascript/coordinates
    const TILE_SIZE = 256;
    let siny = Math.sin((lat * Math.PI) / 180);

    // Truncating to 0.9999 effectively limits latitude to 89.189. This is
    // about a third of a tile past the edge of the world tile.
    siny = Math.min(Math.max(siny, -0.9999), 0.9999);

    return [
      TILE_SIZE * (0.5 + lng / 360),
      TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
    ];
  }

  /**
   * Return coordinates in pixels relative the map element.
   */
  static async toClient(map, lat, lng) {
    let bounds = await map.retrieveBounds();
    log(`[map:util:latlng-to-coords] bounds: ${JSON.stringify(bounds)}`);

    let [bottomLeft_x] = Coords.toWorld(bounds.south, bounds.west);
    let [, topRight_y] = Coords.toWorld(bounds.north, bounds.east);
    let [loc_x, loc_y] = Coords.toWorld(lat, lng);

    let scale = 1 << map.zoom;
    return [
      Math.floor((loc_x - bottomLeft_x) * scale),
      Math.floor((loc_y - topRight_y) * scale),
    ];
  }

  /**
   * Returns distance between two points in km.
   */
  static distanceBetween(p1, p2) {
    const deg2rad = deg => deg * (Math.PI / 180);
    const R = 6371; // Radius of the earth in km
    let dLat = deg2rad(p2.lat - p1.lat); // deg2rad below
    let dLon = deg2rad(p2.lng - p1.lng);
    let a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(p1.lat)) *
        Math.cos(deg2rad(p2.lat)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = R * c; // Distance in km
    return d;
  }

  static slope(p1, p2) {
    let a = this.distanceBetween(p1, p2);
    let b = Math.abs(p1.elevation - p2.elevation) / 1000;
    let slope = Math.atan(b / a);
    return slope;
  }

  /**
   * Returns a distance that corresponds to a single pixel.
   */
  static async distanceInPixel(map) {
    let { south, east, north, west } = await map.retrieveBounds();

    let [topLeft_x, topLeft_y] = Coords.toWorld(north, west);
    let [, bottomLeft_y] = Coords.toWorld(south, west);
    let [topRight_x] = Coords.toWorld(north, east);

    let scale = 1 << map.zoom;
    let gap_lat = (north - south) / ((bottomLeft_y - topLeft_y) * scale);
    let gap_lng = (east - west) / ((topRight_x - topLeft_x) * scale);
    return {
      gap_lat,
      gap_lng,
      south,
      east,
      north,
      west,
    };
  }
}

export { Coords };
